/**
 * Action click handling per SSE_EVENT.md §13: route by handler (chat vs direct)
 * and invoke the appropriate callback. Keeps NewChatWindow thin by moving logic here.
 */

import { ACTION_CONFIG, resolveActionType } from "../config/actionConfig";
import type { UIAction, DraftPost, SocialPostData } from "../types";
import type { ActionClickContext } from "../components/ActionButtons";

export interface ActionHandlerCallbacks {
  sendMessage: (prompt: string) => void;
  openMediaLibrary: () => void;
  savePosts: (posts: DraftPost[]) => void;
  schedulePost: (postData: SocialPostData) => void;
  /** For include_image: open library/upload; on select, attach URL to post at payload.post_index. */
  openMediaLibraryForPost?: (payload?: Record<string, unknown>) => void;
}

/** Resolve display label: backend label > config defaultLabel > type. */
export function getActionDisplayLabel(action: UIAction): string {
  if (action.label) return action.label;
  const config = ACTION_CONFIG[action.type];
  return config?.defaultLabel ?? action.type;
}

/**
 * Execute the correct behavior for a UI action click.
 * Direct actions call API/UI callbacks; chat actions send a message to the chat service.
 * Resolves type from action.label when backend sends wrong/same type for all (so the correct prompt is sent).
 */
export function executeAction(
  action: UIAction,
  context: ActionClickContext | undefined,
  callbacks: ActionHandlerCallbacks
): void {
  const resolvedType = resolveActionType(action);
  const config = ACTION_CONFIG[resolvedType];

  if (config?.handler === "direct") {
    switch (resolvedType) {
      case "view_images_on_library":
        callbacks.openMediaLibrary();
        return;
      case "include_image":
        callbacks.openMediaLibraryForPost?.(action.payload);
        return;
      case "save_post": {
        const posts = context?.generatedPosts?.posts;
        if (posts?.length) {
          const postIndex =
            typeof action.payload?.post_index === "number"
              ? action.payload.post_index
              : 0;
          const post = posts[postIndex];
          if (post) callbacks.savePosts([post]);
        }
        return;
      }
      case "schedule_post": {
        const posts = context?.generatedPosts?.posts;
        if (posts?.length) {
          const postIndex =
            typeof action.payload?.post_index === "number"
              ? action.payload.post_index
              : 0;
          const post = posts[postIndex];
          if (post) {
            const asSocialPost: SocialPostData = {
              title: post.title,
              content: post.content,
              image_url: post.image_url,
              platforms: [post.platform],
              hashtags: Array.isArray(post.hashtags)
                ? (post.hashtags as string[]).join?.(" ") ?? ""
                : "",
              call_to_action: post.call_to_action ?? "",
              scheduled_time: post.scheduled_time ?? "",
            };
            callbacks.schedulePost(asSocialPost);
          }
        }
        return;
      }
      default:
        break;
    }
    return;
  }

  // Chat handler: send the message to the chat service.
  // Prefer explicit `message` when label is display-only; else button label (backend contextual text);
  // else config prompt; else type.
  const explicit = typeof action.message === "string" ? action.message.trim() : "";
  const fromLabel = action.label && action.label.trim() ? action.label.trim() : "";
  const prompt =
    explicit ||
    fromLabel ||
    (config?.prompt ?? "").trim() ||
    action.type;
  if (prompt) callbacks.sendMessage(prompt);
}
